import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  ArcElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import UsersSection from './UsersSection';
import { API } from '../api';
import './UsersTab.css';
import { useTheme } from '../context/ThemeContext';
import { getChartTheme } from '../utils/theme';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

const UsersTab = ({
  users,
  pagination,
  stats,
  loading,
  currency,
  onUserClick,
  onAddHours,
  onViewPayments,
  onPageChange
}) => {
  const [chartData, setChartData] = useState(null);
  const lineChartRef = useRef(null);
  const lineChartInstanceRef = useRef(null);
  const { theme } = useTheme();
  const chartColorsRef = useRef(getChartTheme(theme));

  const mau = stats && stats.users && stats.users.ma !== undefined ? stats.users.ma : 0;
  const dau = stats && stats.users && stats.users.da !== undefined ? stats.users.da : 0;
  const onlineUsers = stats && stats.users && stats.users.online !== undefined ? stats.users.online : 0;
  const totalUsersStats = stats && stats.users && stats.users.total !== undefined ? stats.users.total : (pagination ? pagination.total : users.length);
  const viewsToday = stats && stats.summaries && stats.summaries.views && stats.summaries.views.today !== undefined ? stats.summaries.views.today : 0;
  
  useEffect(() => {
    const loadChartData = async () => {
      const result = await API.stats.getUsersChart();
      if (result.success) {
        setChartData(result.data);
      }
    };
    loadChartData();
  }, []);

  useEffect(() => {
    chartColorsRef.current = getChartTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (!lineChartRef.current || !chartData) return;

    const ctx = lineChartRef.current.getContext('2d');

    if (lineChartInstanceRef.current) {
      lineChartInstanceRef.current.destroy();
    }

    const data = chartData.values || [];
    const labels = chartData.labels || [];
    const colors = chartColorsRef.current;

    lineChartInstanceRef.current = new ChartJS(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Новые пользователи',
            data: data,
            borderColor: colors.stroke,
            backgroundColor: colors.fill,
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: colors.pointBg,
            pointBorderColor: colors.stroke,
            pointBorderWidth: 2,
            pointHoverRadius: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: colors.tooltipBackground,
            padding: 12,
            titleFont: { size: 13, weight: 'bold' },
            bodyFont: { size: 12 },
            borderColor: colors.tooltipBorder,
            borderWidth: 1,
            titleColor: colors.tooltipText,
            bodyColor: colors.tooltipText,
            displayColors: false,
            callbacks: {
              label: (context) => {
                return `${context.parsed.y} пользователей`;
              }
            },
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grace: '10%',
            grid: { color: colors.grid, drawBorder: true },
            ticks: { 
              color: colors.ticks, 
              font: { size: 12 },
              precision: 0
            }
          },
          x: {
            grid: { display: false },
            ticks: { color: colors.ticks, font: { size: 12 } }
          }
        }
      }
    });

    return () => {
      if (lineChartInstanceRef.current) {
        lineChartInstanceRef.current.destroy();
        lineChartInstanceRef.current = null;
      }
    };
  }, [chartData, theme]);

  return (
    <div className="tab-content">
      {/* Stats Grid */}
      <div className="users-stats-grid">
        <div className="stat-card">
          <div className="stat-label">Всего пользователей</div>
          <div className="stat-value">{totalUsersStats.toLocaleString('ru-RU')}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">MAU</div>
          <div className="stat-value">{mau.toLocaleString('ru-RU')}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">DAU</div>
          <div className="stat-value">{dau.toLocaleString('ru-RU')}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Онлайн сейчас</div>
          <div className="stat-value">{onlineUsers.toLocaleString('ru-RU')}</div>
          <div className="stat-subtext">Активные сессии</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Просмотры сегодня</div>
          <div className="stat-value">{viewsToday.toLocaleString('ru-RU')}</div>
          <div className="stat-subtext">Саммари просмотрено</div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-container">
        <div className="chart-card">
          <h3>Прирост пользователей за неделю</h3>
          {chartData ? (
            <canvas ref={lineChartRef}></canvas>
          ) : (
            <div className="empty-chart-message">Нет данных для отображения графика</div>
          )}
        </div>
      </div>
      {/* Users Table */}
      <UsersSection
        users={users}
        pagination={pagination}
        loading={loading}
        onAddHours={onAddHours}
        onViewPayments={onViewPayments}
        currency={currency}
        onUserNameClick={onUserClick}
        showPagination={true}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default UsersTab;
